import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Combobox, Listbox } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/outline";
import { cn } from "./cn";
import FloatingPanel from "./FloatingPanel";

/**
 * Bun form controls. Spec: Component Library "Inputs & forms".
 *
 * <Field label helper error>...</Field> wraps any control with the standard
 * label (12/600), helper (11/450 stone) and error (11/550 danger) rows.
 * `labelAction` puts a small action on the label row's right edge (the
 * canvas "Forgot password?" pattern). <Input error /> and <Textarea error />
 * are the text controls themselves.
 */
export function Field({ label, labelAction, helper, error, htmlFor, className, children }) {
  const labelEl = label && (
    <label
      htmlFor={htmlFor}
      className={cn(
        "text-bun-meta font-semibold text-bun-text",
        labelAction && "flex-1",
        error && "text-bun-danger"
      )}
    >
      {label}
    </label>
  );
  return (
    <div className={cn("flex flex-col gap-[6px] font-instrument", className)}>
      {label &&
        (labelAction ? (
          <div className="flex items-baseline">
            {labelEl}
            {labelAction}
          </div>
        ) : (
          labelEl
        ))}
      {children}
      {error ? (
        <span className="text-bun-label font-strong normal-case tracking-normal text-bun-danger">
          {error}
        </span>
      ) : (
        helper && (
          <span className="text-bun-label font-book normal-case tracking-normal text-bun-stone">
            {helper}
          </span>
        )
      )}
    </div>
  );
}

export const INPUT_BASE =
  "w-full min-h-11 sm:min-h-0 box-border font-instrument text-bun-body font-book text-bun-text bg-bun-surface " +
  "border border-bun-line rounded-bun px-3 py-[9px] outline-none placeholder:text-bun-faint " +
  "focus:border-bun-blue focus:ring-[3px] focus:ring-bun-ring " +
  "disabled:bg-bun-mist disabled:text-bun-stone disabled:cursor-not-allowed";

export const INPUT_ERROR =
  "border-bun-danger-input ring-[3px] ring-bun-danger-bg focus:border-bun-danger-input focus:ring-bun-danger-bg";

export const INPUT_COMPACT = "rounded-bun-sm px-2 py-1 leading-5";

export const Input = forwardRef(function Input({ error, size = "md", variant, className, ...props }, ref) {
  return (
    <input
      ref={ref}
      className={cn(INPUT_BASE, size === "sm" && INPUT_COMPACT, (props.type === 'date' || props.type === 'month') && 'bun-native-calendar', variant === 'inline' && 'border-0 rounded-none bg-transparent px-0 focus:ring-0 focus-visible:outline-none', error && INPUT_ERROR, className)}
      {...props}
    />
  );
});

export const Textarea = forwardRef(function Textarea(
  { error, className, rows = 3, size = "md", ...props },
  ref
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(INPUT_BASE, "leading-relaxed resize-y", size === "sm" && INPUT_COMPACT, error && INPUT_ERROR, className)}
      {...props}
    />
  );
});

function selectOptions(children, disabled = false) {
  return React.Children.toArray(children).flatMap((child) => {
    if (!React.isValidElement(child)) return [];
    if (child.type === "option") {
      const label = child.props.label ?? child.props.children;
      return [
        {
          value: String(child.props.value ?? label),
          label,
          description: child.props['data-description'],
          disabled: disabled || child.props.disabled,
        },
      ];
    }
    const options = selectOptions(child.props.children, disabled || child.props.disabled);
    return child.type === "optgroup" ? [{ label: child.props.label, options }] : options;
  });
}

function SelectOpenState({ open, autoOpen, onOpenChange, triggerRef, disabled }) {
  const previousOpen = useRef(open);
  const didAutoOpen = useRef(false);
  useEffect(() => {
    if (previousOpen.current === open) return;
    previousOpen.current = open;
    onOpenChange?.(open);
  }, [open, onOpenChange]);
  useEffect(() => {
    if (autoOpen && !disabled && !didAutoOpen.current) {
      didAutoOpen.current = true;
      triggerRef.current?.click();
    }
  }, [autoOpen, disabled, triggerRef]);
  return null;
}

function DropdownSearchFocus({ open, inputRef, triggerRef }) {
  const previousOpen = useRef(open);
  useEffect(() => {
    if (!open) return undefined;
    const frame = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [open, inputRef]);
  useEffect(() => {
    if (previousOpen.current && !open && document.activeElement === document.body) triggerRef.current?.focus({ preventScroll: true });
    previousOpen.current = open;
  }, [open, triggerRef]);
  return null;
}

function SearchableSelectControl({ options, selected, onChange, anchorRef, forwardedRef, disabled, error, required, size, menuWidth, searchPlaceholder, searchPlacement, query, setQuery, autoOpen, onOpenChange, ...props }) {
  const toggleRef = useRef(null);
  const searchRef = useRef(null);
  const searchInDropdown = searchPlacement === 'dropdown';
  const filtered = options.filter(option => `${option.label} ${option.description || ''}`.toLowerCase().includes(query.toLowerCase()));
  const choices = <>
    {filtered.length === 0 && <li role="presentation" className="px-3 py-2 text-bun-body text-bun-stone">No results found</li>}
    {filtered.map((option, index) => <Combobox.Option key={`${option.value}-${index}`} value={option.value} disabled={option.disabled}
      className={({ active }) => cn('flex min-h-11 cursor-pointer items-center gap-3 rounded-bun px-3 py-2 text-bun-body', active && 'bg-bun-mist', option.disabled && 'cursor-not-allowed text-bun-faint')}>
      {({ selected: isSelected }) => <>
        <span className="min-w-0 flex-1">
          <span className={cn('block', isSelected && 'font-semibold text-bun-blue')}>{option.label}</span>
          {option.description && <span className="block text-bun-label text-bun-stone">{option.description}</span>}
        </span>
        {isSelected && <CheckIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-bun-blue" />}
      </>}
    </Combobox.Option>)}
  </>;
  return <Combobox value={selected?.value ?? ''} disabled={disabled} onChange={next => {
    setQuery('');
    onChange(next);
  }}>
    {({ open }) => <>
      <SelectOpenState open={open} autoOpen={autoOpen} onOpenChange={nextOpen => {
        if (!nextOpen) setQuery('');
        onOpenChange?.(nextOpen);
      }} triggerRef={toggleRef} disabled={disabled} />
      {searchInDropdown && <DropdownSearchFocus open={open} inputRef={searchRef} triggerRef={toggleRef} />}
      <div ref={anchorRef} className="relative">
        {searchInDropdown ? <Combobox.Button {...props} ref={node => { toggleRef.current = node; forwardedRef(node); }}
          role="combobox" onClick={() => setQuery('')}
          aria-required={required || undefined} aria-invalid={error || props['aria-invalid'] || undefined}
          className={cn(INPUT_BASE, 'flex items-center justify-between gap-3 text-left cursor-pointer', size === 'sm' && INPUT_COMPACT, error && INPUT_ERROR)}>
          <span className="min-w-0 truncate">{selected?.label ?? ''}</span>
          <ChevronDownIcon aria-hidden="true" className={cn('h-4 w-4 shrink-0 text-bun-stone', open && 'rotate-180')} />
        </Combobox.Button> : <><Combobox.Input
          {...props}
          ref={forwardedRef}
          displayValue={() => selected?.value === '' ? '' : selected?.label ?? ''}
          onChange={event => setQuery(event.target.value)}
          onBlur={props.onBlur}
          placeholder={searchPlaceholder}
          aria-required={required || undefined}
          aria-invalid={error || props['aria-invalid'] || undefined}
          className={cn(INPUT_BASE, size === 'sm' && INPUT_COMPACT, 'pr-11', error && INPUT_ERROR)}
        />
        <Combobox.Button ref={toggleRef} aria-label={`Toggle ${props['aria-label'] || 'selection'} options`} onClick={() => setQuery('')} className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-bun text-bun-stone focus-visible:outline focus-visible:outline-bun-blue disabled:cursor-not-allowed">
          <ChevronDownIcon aria-hidden="true" className={cn('h-4 w-4', open && 'rotate-180')} />
        </Combobox.Button></>}
      </div>
      <FloatingPanel as={searchInDropdown ? 'div' : Combobox.Options} {...(!searchInDropdown && { static: true })} open={open} anchorRef={anchorRef}
        portalTarget={anchorRef.current?.closest('[data-bun-popover-panel], [data-bun-dialog-panel]')}
        aria-label={props['aria-label'] ? `${props['aria-label']} options` : 'Options'}
        width={menuWidth} minWidth={192} maxHeight={320} align="start" className="m-0 list-none p-1.5 focus:outline-none">
        {searchInDropdown ? <>
          <div className="sticky top-0 bg-bun-surface pb-1.5">
            <Combobox.Input ref={searchRef} displayValue={() => ''} onChange={event => setQuery(event.target.value)}
              aria-label={searchPlaceholder} placeholder={searchPlaceholder}
              className={cn(INPUT_BASE, INPUT_COMPACT)} />
          </div>
          <Combobox.Options static aria-label={props['aria-label'] ? `${props['aria-label']} options` : 'Options'}
            className="m-0 list-none p-0">{choices}</Combobox.Options>
        </> : choices}
      </FloatingPanel>
    </>}
  </Combobox>;
}

/** Custom single-choice dropdown. Keep option children and native change events for forms. */
export const Select = forwardRef(function Select(
  {
    error,
    size = "md",
    menuWidth = "anchor",
    className,
    children,
    value,
    defaultValue,
    onChange,
    disabled = false,
    required,
    name,
    form,
    onInvalid,
    searchable = false,
    searchPlacement = 'trigger',
    autoOpen = false,
    onOpenChange,
    searchPlaceholder = 'Search options',
    wrapLabel = false,
    ...props
  },
  ref,
) {
  const anchorRef = useRef(null);
  const formControlRef = useRef(null);
  const tabDirection = useRef(null);
  const searchableInputRef = useRef(null);
  const resetSelectionRef = useRef(null);
  const entries = selectOptions(children);
  const options = entries.flatMap((entry) => entry.options ?? [entry]);
  const initialValue = String(
    defaultValue ?? options.find((option) => !option.disabled)?.value ?? "",
  );
  const [internalValue, setInternalValue] = useState(initialValue);
  const [invalid, setInvalid] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const currentValue = String(value ?? internalValue);
  // Match a native single select when asynchronously loaded options replace its value.
  const selected =
    options.find((option) => option.value === currentValue) ??
    options.find((option) => !option.disabled);
  const selectedValue = selected?.value ?? "";
  const resetOption = options.find(option => option.value === (value === undefined ? initialValue : selectedValue))
    ?? options.find(option => !option.disabled);
  resetSelectionRef.current = { value: resetOption?.value ?? '', label: resetOption?.label ?? '' };
  const changeValue = (nextValue) => {
    const control = formControlRef.current;
    control.value = nextValue;
    control.dispatchEvent(new Event('change', { bubbles: true }));
  };

  useEffect(() => {
    const ownerForm = formControlRef.current?.form;
    const reset = () => {
      if (value === undefined) setInternalValue(initialValue);
      setInvalid(false);
      // Native reset runs after this event and can clear an unchanged Combobox input.
      // Restore both visible and submitted values after that default action completes.
      queueMicrotask(() => {
        const control = formControlRef.current;
        if (!control) return;
        setSearchQuery('');
        // The form owner's onReset may have supplied a new controlled value.
        const resetSelection = resetSelectionRef.current;
        control.value = resetSelection.value;
        if (searchableInputRef.current) searchableInputRef.current.value = resetSelection.value === '' ? '' : resetSelection.label;
      });
    };
    ownerForm?.addEventListener("reset", reset);
    return () => ownerForm?.removeEventListener("reset", reset);
  }, [form, initialValue, value]);

  const renderOption = (option) => (
    <Listbox.Option
      key={options.indexOf(option)}
      value={option.value}
      disabled={option.disabled}
      className={({ active }) =>
        cn(
          "flex min-h-11 cursor-pointer items-center gap-3 rounded-bun px-3 py-2 text-bun-body",
          active && "bg-bun-mist",
          option.disabled && "cursor-not-allowed text-bun-faint",
        )
      }
    >
      {({ selected: isSelected }) => (
        <>
          <span
            className={cn(
              "min-w-0 flex-1 break-words",
              isSelected && "font-semibold text-bun-blue",
            )}
          >
            {option.label}
            {option.description && <span className="mt-0.5 block text-bun-label font-book text-bun-stone">{option.description}</span>}
          </span>
          {isSelected && (
            <CheckIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-bun-blue" />
          )}
        </>
      )}
    </Listbox.Option>
  );

  return (
    <div className={cn("relative min-w-0", className)}>
      {searchable ? <SearchableSelectControl {...props} options={options} selected={selected}
        autoOpen={autoOpen} onOpenChange={onOpenChange}
        size={size} menuWidth={menuWidth} searchPlacement={searchPlacement}
        query={searchQuery} setQuery={setSearchQuery}
        disabled={disabled} required={required} error={error || invalid} searchPlaceholder={searchPlaceholder}
        anchorRef={anchorRef} forwardedRef={node => {
          searchableInputRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }} onChange={changeValue} /> : <Listbox
        value={selectedValue}
        disabled={disabled}
        onChange={changeValue}
      >
        {({ open }) => (
          <>
            <SelectOpenState open={open} autoOpen={autoOpen} onOpenChange={onOpenChange} triggerRef={anchorRef} disabled={disabled} />
            <Listbox.Button
              {...props}
              ref={(node) => {
                anchorRef.current = node;
                if (typeof ref === "function") ref(node);
                else if (ref) ref.current = node;
              }}
              role="combobox"
              value={selectedValue}
              onFocus={(event) => {
                props.onFocus?.(event);
                if (tabDirection.current === null) return;
                const direction = tabDirection.current;
                tabDirection.current = null;
                const scope = anchorRef.current.closest('[role="dialog"]') ?? document.body;
                const stops = Array.from(
                  scope.querySelectorAll("button, input, select, textarea, a[href], [tabindex]"),
                ).filter(
                  (node) =>
                    node.tabIndex >= 0 &&
                    !node.disabled &&
                    node.getClientRects().length > 0 &&
                    !node.closest('[inert], [aria-hidden="true"]') &&
                    getComputedStyle(node).visibility !== "hidden",
                );
                const next = stops[stops.indexOf(anchorRef.current) + direction];
                next?.focus();
              }}
              aria-required={required || undefined}
              aria-invalid={error || invalid || props["aria-invalid"] || undefined}
              className={cn(
                INPUT_BASE,
                "flex items-center justify-between gap-3 text-left cursor-pointer",
                size === "sm" && `${INPUT_COMPACT} gap-1.5`,
                (error || invalid) && INPUT_ERROR,
              )}
            >
              <span className={cn("min-w-0", wrapLabel ? "whitespace-normal break-words" : "truncate")}>{selected?.label ?? ""}</span>
              <ChevronDownIcon
                aria-hidden="true"
                className={cn("h-4 w-4 shrink-0 text-bun-stone", open && "rotate-180")}
              />
            </Listbox.Button>
            <FloatingPanel
              as={Listbox.Options}
              static
              autoFocus
              open={open}
              anchorRef={anchorRef}
              portalTarget={anchorRef.current?.closest("[data-bun-popover-panel], [data-bun-dialog-panel]")}
              aria-label={props["aria-label"] ? `${props["aria-label"]} options` : "Options"}
              width={menuWidth}
              minWidth={192}
              maxHeight={320}
              align="start"
              className="m-0 list-none p-1.5 [scrollbar-width:thin] [scrollbar-color:var(--bun-dim)_transparent] focus:outline-none"
              onKeyDown={(event) => {
                // Close through the persistent trigger so unmounting the portal cannot cancel focus restoration.
                if (event.key !== "Tab" && event.key !== "Escape") return;
                event.preventDefault();
                event.stopPropagation();
                if (event.key === "Tab") tabDirection.current = event.shiftKey ? -1 : 1;
                anchorRef.current.click();
              }}
            >
              {entries.map((entry, index) =>
                entry.options ? (
                  <li key={`${entry.label}-${index}`} role="group" aria-label={entry.label}>
                    <div
                      aria-hidden="true"
                      className="px-3 py-2 text-bun-label font-semibold text-bun-stone"
                    >
                      {entry.label}
                    </div>
                    <ul role="presentation" className="m-0 list-none p-0">
                      {entry.options.map(renderOption)}
                    </ul>
                  </li>
                ) : (
                  renderOption(entry)
                ),
              )}
            </FloatingPanel>
          </>
        )}
      </Listbox>}
      {/* This non-interactive backing control preserves validation, FormData, and real change events. */}
      <select
        ref={formControlRef}
        hidden
        aria-hidden="true"
        tabIndex={-1}
        name={name}
        form={form}
        required={required}
        disabled={disabled}
        value={selectedValue}
        onChange={(event) => {
          if (value === undefined) setInternalValue(event.target.value);
          setInvalid(false);
          onChange?.(event);
        }}
        onInvalid={(event) => {
          event.preventDefault();
          setInvalid(true);
          (searchable ? searchableInputRef.current : anchorRef.current)?.focus();
          onInvalid?.(event);
        }}
      >
        {options.map((option, index) => (
          <option key={index} value={option.value} disabled={option.disabled} />
        ))}
      </select>
    </div>
  );
});
